import { useAuth } from '@choco-admin/auth';
import { Spinner } from '@heroui/react';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface AuthGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * AuthGuard Component
 *
 * Protects routes by checking authentication status.
 * Redirects unauthenticated users to login page.
 * Shows loading state while authentication is being checked.
 */
const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-default-50">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-foreground-600 text-base">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default AuthGuard;
